import React, { useState, useEffect, useCallback } from 'react';
import { promotionsService } from '../services/firestoreService';

const LOCAL_STORAGE_KEY = 'promotions';

// One-time migration: read from localStorage (for backward compatibility)
const loadPromotionsFromLocalStorage = () => {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (err) {
    console.error('Error reading promotions from localStorage:', err);
  }
  return [];
};

const Promotions = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    discount: '',
    startDate: '',
    endDate: '',
    code: ''
  });

  const loadPromotions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await promotionsService.get();
      if (items.length > 0) {
        setPromotions(items);
        setLoading(false);
        return;
      }
      const fromStorage = loadPromotionsFromLocalStorage();
      if (fromStorage.length > 0) {
        await promotionsService.set(fromStorage);
        setPromotions(fromStorage);
        try {
          localStorage.removeItem(LOCAL_STORAGE_KEY);
        } catch (_) {}
      } else {
        setPromotions([]);
      }
    } catch (err) {
      console.error('Error loading promotions:', err);
      setError(err.message || 'Failed to load promotions');
      setPromotions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPromotions();
  }, [loadPromotions]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAdd = () => {
    setIsAdding(true);
    setFormData({ 
      title: '', 
      description: '', 
      discount: '', 
      startDate: '', 
      endDate: '', 
      code: '' 
    });
  };

  const handleEdit = (promotion) => {
    setEditingId(promotion.id);
    setFormData({
      title: promotion.title || '',
      description: promotion.description || '',
      discount: promotion.discount || '',
      startDate: promotion.startDate || '',
      endDate: promotion.endDate || '',
      code: promotion.code || ''
    });
    setIsAdding(false);
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({ 
      title: '', 
      description: '', 
      discount: '', 
      startDate: '', 
      endDate: '', 
      code: '' 
    });
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      alert('Title is required');
      return;
    }

    const newPromotion = {
      id: editingId || Date.now().toString(),
      title: formData.title.trim(),
      description: formData.description.trim(),
      discount: formData.discount.trim(),
      startDate: formData.startDate,
      endDate: formData.endDate,
      code: formData.code.trim(),
      createdAt: editingId 
        ? promotions.find(p => p.id === editingId)?.createdAt || new Date().toISOString()
        : new Date().toISOString()
    };

    let updatedPromotions;
    if (editingId) {
      updatedPromotions = promotions.map(promotion => 
        promotion.id === editingId ? newPromotion : promotion
      );
      setEditingId(null);
    } else {
      updatedPromotions = [...promotions, newPromotion];
      setIsAdding(false);
    }

    setPromotions(updatedPromotions);
    setError(null);
    try {
      await promotionsService.set(updatedPromotions);
    } catch (err) {
      console.error('Error saving promotions:', err);
      setError(err.message || 'Failed to save');
      return;
    }
    setFormData({ 
      title: '', 
      description: '', 
      discount: '', 
      startDate: '', 
      endDate: '', 
      code: '' 
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this promotion?')) return;
    const updatedPromotions = promotions.filter(promotion => promotion.id !== id);
    setPromotions(updatedPromotions);
    setError(null);
    try {
      await promotionsService.set(updatedPromotions);
    } catch (err) {
      console.error('Error saving promotions:', err);
      setError(err.message || 'Failed to delete');
      setPromotions(promotions);
    }
  };

  const isActive = (promotion) => {
    if (!promotion.startDate && !promotion.endDate) return true;
    const now = new Date();
    const start = promotion.startDate ? new Date(promotion.startDate) : null;
    const end = promotion.endDate ? new Date(promotion.endDate) : null;
    
    if (start && now < start) return false;
    if (end && now > end) return false;
    return true;
  };

  if (loading) {
    return (
      <div className="vehicle-inner">
        <div className="vehicle-header">
          <h2>Promotions</h2>
          <p className="vehicle-subtitle">Loading…</p>
        </div>
        <div className="empty-state">Loading promotions…</div>
      </div>
    );
  }

  return (
    <div className="vehicle-inner" style={{ position: 'relative' }}>
      {error && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '16px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid #ef4444',
          borderRadius: '8px',
          color: '#b91c1c'
        }}>
          {error}
        </div>
      )}
      {/* Coming Soon Overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '16px'
      }}>
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '3rem 4rem',
          borderRadius: '16px',
          boxShadow: 'var(--shadow-lg)',
          textAlign: 'center',
          border: '2px solid var(--border)'
        }}>
          <div style={{
            fontSize: '3rem',
            fontWeight: 700,
            color: 'var(--text-dark)',
            marginBottom: '1rem'
          }}>
            Coming Soon
          </div>
          <div style={{
            fontSize: 'var(--font-base)',
            color: 'var(--text-muted)',
            maxWidth: '400px'
          }}>
            This feature is currently under development and will be available soon.
          </div>
        </div>
      </div>

      <div style={{ opacity: 0.3, pointerEvents: 'none' }}>
      <div className="vehicle-header">
        <div>
          <h2>Promotions</h2>
          <p className="vehicle-subtitle">Manage promotional offers and discounts</p>
        </div>
        {!isAdding && !editingId && (
          <button 
            type="button" 
            className="hero-button hero-primary"
            onClick={handleAdd}
          >
            Add Promotion
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {(isAdding || editingId) && (
        <div className="link-form-card" style={{ 
          marginBottom: '24px', 
          padding: '20px', 
          background: 'var(--card-bg)', 
          borderRadius: '12px',
          border: '1px solid var(--border-color)'
        }}>
          <h3 style={{ marginBottom: '16px', fontSize: '18px' }}>
            {editingId ? 'Edit Promotion' : 'Add New Promotion'}
          </h3>
          <div className="form-row" style={{ marginBottom: '12px' }}>
            <label htmlFor="promoTitle">Title *</label>
            <input
              id="promoTitle"
              name="title"
              type="text"
              placeholder="e.g., Summer Sale"
              value={formData.title}
              onChange={handleInputChange}
              style={{ width: '100%', padding: '8px 12px' }}
            />
          </div>
          <div className="form-row" style={{ marginBottom: '12px' }}>
            <label htmlFor="promoDescription">Description</label>
            <textarea
              id="promoDescription"
              name="description"
              placeholder="Promotion details..."
              value={formData.description}
              onChange={handleInputChange}
              rows="3"
              style={{ width: '100%', padding: '8px 12px', resize: 'vertical' }}
            />
          </div>
          <div className="form-row" style={{ marginBottom: '12px' }}>
            <label htmlFor="promoDiscount">Discount</label>
            <input
              id="promoDiscount"
              name="discount"
              type="text"
              placeholder="e.g., 20% off or £50 off"
              value={formData.discount}
              onChange={handleInputChange}
              style={{ width: '100%', padding: '8px 12px' }}
            />
          </div>
          <div className="form-row" style={{ marginBottom: '12px' }}>
            <label htmlFor="promoCode">Promo Code</label>
            <input
              id="promoCode"
              name="code"
              type="text"
              placeholder="e.g., SUMMER2024"
              value={formData.code}
              onChange={handleInputChange}
              style={{ width: '100%', padding: '8px 12px' }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <div className="form-row">
              <label htmlFor="promoStartDate">Start Date</label>
              <input
                id="promoStartDate"
                name="startDate"
                type="date"
                value={formData.startDate}
                onChange={handleInputChange}
                style={{ width: '100%', padding: '8px 12px' }}
              />
            </div>
            <div className="form-row">
              <label htmlFor="promoEndDate">End Date</label>
              <input
                id="promoEndDate"
                name="endDate"
                type="date"
                value={formData.endDate}
                onChange={handleInputChange}
                style={{ width: '100%', padding: '8px 12px' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button 
              type="button" 
              className="primary-btn"
              onClick={handleSave}
            >
              Save
            </button>
            <button 
              type="button" 
              className="secondary-btn"
              onClick={handleCancel}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Promotions Grid */}
      <div className="links-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '16px'
      }}>
        {promotions.length === 0 ? (
          <div className="empty-state">No promotions added yet. Click "Add Promotion" to get started.</div>
        ) : (
          promotions.map(promotion => {
            const active = isActive(promotion);
            return (
              <div
                key={promotion.id}
                className="link-card"
                style={{
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  opacity: active ? 1 : 0.6
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    marginBottom: '8px'
                  }}>
                    <h3 style={{ 
                      margin: 0, 
                      fontSize: '18px', 
                      fontWeight: 600,
                      color: 'var(--text-dark)'
                    }}>
                      {promotion.title}
                    </h3>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 600,
                      background: active ? '#10B981' : '#6B7280',
                      color: 'white'
                    }}>
                      {active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {promotion.description && (
                    <p style={{ 
                      margin: '0 0 8px 0', 
                      fontSize: '14px', 
                      color: 'var(--text-muted)',
                      lineHeight: '1.5'
                    }}>
                      {promotion.description}
                    </p>
                  )}
                  {promotion.discount && (
                    <div style={{ 
                      fontSize: '16px', 
                      fontWeight: 600,
                      color: 'var(--primary)',
                      marginBottom: '8px'
                    }}>
                      {promotion.discount}
                    </div>
                  )}
                  {promotion.code && (
                    <div style={{ 
                      fontSize: '14px', 
                      color: 'var(--text-muted)',
                      marginBottom: '8px',
                      fontFamily: 'monospace',
                      background: 'var(--card-bg)',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      border: '1px solid var(--border-color)',
                      display: 'inline-block'
                    }}>
                      Code: {promotion.code}
                    </div>
                  )}
                  {(promotion.startDate || promotion.endDate) && (
                    <div style={{ 
                      fontSize: '12px', 
                      color: 'var(--text-muted)',
                      marginTop: '8px'
                    }}>
                      {promotion.startDate && `From: ${new Date(promotion.startDate).toLocaleDateString()}`}
                      {promotion.startDate && promotion.endDate && ' • '}
                      {promotion.endDate && `Until: ${new Date(promotion.endDate).toLocaleDateString()}`}
                    </div>
                  )}
                </div>
                <div style={{ 
                  display: 'flex', 
                  gap: '8px',
                  paddingTop: '8px',
                  borderTop: '1px solid var(--border-color)'
                }}>
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => handleEdit(promotion)}
                    style={{ flex: 1, fontSize: '12px', padding: '6px 12px' }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => handleDelete(promotion.id)}
                    style={{ flex: 1, fontSize: '12px', padding: '6px 12px', color: '#cf3c5a' }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
      </div>
    </div>
  );
};

export default Promotions;

